import type { ConstantParams } from '@/store/types';
import type { Payload, ToReturn } from './Api';

export type CalendarEvent = 'snapshot' | 'command' | 'preview';
export type CalendarReply = {
  ok: boolean;
  err: number;
  err_txt: string;
  code?: string;
  message: string;
  [key: string]: unknown;
};

// The receiving procedure accepts varchar(max). JSON escapes preserve Unicode
// and keep text from colliding with the legacy dispatcher's separators.
export const stringifySqlJson = (value: unknown): string => JSON.stringify(value)
  .replace(/[\u007f-\uffff'<>&~(]/g, (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);

export const calendarFailure = (code: string, message: string): CalendarReply => ({
  ok: false, err: 9101, err_txt: message, code, message,
});

export const contextChanged = (): CalendarReply => (
  calendarFailure('CONTEXT_CHANGED', 'Контекст календаря изменился.')
);

// Ответ, который можно безопасно читать: ok/message заполнены всегда
export const normalizeCalendarReply = (result: unknown): CalendarReply => {
  const reply = result as CalendarReply | null;
  if (!reply || typeof reply.ok !== 'boolean') {
    return calendarFailure('INVALID_RESPONSE', 'Сервер вернул некорректный ответ.');
  }
  const message = reply.err > 0 && typeof reply.err_txt === 'string'
    ? reply.err_txt
    : reply.message;
  return { ...reply, message };
};

export const createRequestId = (): string => {
  // randomUUID нет в типах TS 4.5 и в старых WebView
  const { crypto } = globalThis as typeof globalThis & { crypto: { randomUUID?: () => string } };
  if (crypto.randomUUID) return crypto.randomUUID();
  // Фолбэк UUID v4 для окружений без randomUUID
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] % 16) + 64;
  bytes[8] = (bytes[8] % 64) + 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20),
  ].join('-');
};

export const makeCalendarRequest = (
  event: CalendarEvent,
  payload: unknown,
  requestId: string,
  auth: ConstantParams,
  eventNames: Record<CalendarEvent, string>,
): Payload => ({
  Name_app: auth.Name_app,
  TK: auth.TK,
  IMEI: auth.IMEI,
  id_login: auth.id_login,
  Name_module: 'module_calendar',
  Name_event: eventNames[event],
  json_param: stringifySqlJson({ request_id: requestId, payload }),
});

// Api already unwraps the relay's body into data. SQL returns one row;
// result_json holds the calendar response without depending on a second recordset.
export const readCalendarReply = (response: ToReturn | null): CalendarReply => {
  if (!response || response.errorType) {
    return calendarFailure('REQUEST_FAILED', response?.errorText || 'Не удалось получить ответ сервера.');
  }
  const row = response.data?.[0] as Record<string, unknown> | undefined;
  if (!row) return calendarFailure('INVALID_RESPONSE', 'Сервер вернул пустой ответ календаря.');

  const err = Number(row.err);
  if (Number.isInteger(err) && err !== 0) {
    const message = String(row.err_txt || row.err_text || 'Операция не выполнена.');
    // Preserve revision conflict / other SQL codes when the result is present.
    let detail: Record<string, unknown> = {};
    try { detail = typeof row.result_json === 'string' ? JSON.parse(row.result_json) : {}; } catch { /* Use the public error fields. */ }
    return {
      ...detail, ok: false, err, err_txt: message, message,
    };
  }

  try {
    const result = typeof row.result_json === 'string' ? JSON.parse(row.result_json) : null;
    if (!result || typeof result.ok !== 'boolean' || !Number.isInteger(result.err)
      || typeof result.err_txt !== 'string') {
      return calendarFailure('INVALID_RESPONSE', 'Сервер вернул некорректный ответ календаря.');
    }
    return { ...result, message: result.err ? result.err_txt : result.message || '' };
  } catch {
    return calendarFailure('INVALID_RESPONSE', 'Не удалось прочитать ответ календаря.');
  }
};
